import { FunnelIcon } from '@phosphor-icons/react';
import { useGT } from 'gt-react';
import type {
  WorkbookColumnType,
  WorkbookColumnarSheet,
} from '@erp/shared-types';
import type { ShownDistinctValues } from './sampleDistinctValues';
import type { SheetPlan } from '@erp/workbooks';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import { WorksheetFilters } from './WorksheetFilters';
import type { ChangeSheet } from './useWorkbookEditor';
import type { NotePendingFilter } from './worksheetValueList';
import { WorksheetColumnValidations } from './WorksheetColumnValidations';
import type { ValidationState } from './worksheetValidations';

export function WorksheetColumnMenu({
  sheet,
  sheetPlan,
  column,
  readOnly,
  change,
  input = false,
  removals,
  stale,
  distinct,
  notePending,
  validations,
}: {
  sheet: WorkbookColumnarSheet;
  sheetPlan?: SheetPlan;
  column: string;
  readOnly: boolean;
  change: ChangeSheet;
  input?: boolean;
  /** Rows each filter removed in the Result's Run; see WorksheetFilters. */
  removals?: readonly (number | undefined)[];
  stale?: boolean;
  /** The column's Distinct values in the shown Result (or a preview's sample), for its value list. */
  distinct?: ShownDistinctValues;
  /** Told of value list changes that filter rows out; see WorksheetFilters. */
  notePending?: NotePendingFilter;
  /** An output column's Validations: its Declared type and the shown Result's checks. */
  validations?: {
    type: WorkbookColumnType;
    states: ReadonlyMap<string, ValidationState>;
  };
}) {
  const gt = useGT();
  const filtered = sheet.filters.some((filter) => filter.column === column);
  const label = input
    ? gt('Filter input column {column}', {
        context: 'worksheet column filter menu',
        column,
      })
    : gt('Filter output column {column}', {
        context: 'worksheet column filter menu',
        column,
      });
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          size="icon"
          variant="ghost"
          className="mr-1 size-6 shrink-0"
          aria-label={label}
          title={label}
        >
          <FunnelIcon
            size={14}
            weight={filtered ? 'fill' : 'regular'}
            aria-label={
              filtered
                ? gt('Filter applied', {
                    context: 'worksheet column filter indicator',
                  })
                : undefined
            }
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 max-w-[calc(100vw-2rem)] max-h-[70vh] overflow-auto space-y-3 p-3"
        aria-label={label}
      >
        <h4 className="text-sm font-medium">{column}</h4>
        <fieldset disabled={readOnly}>
          <WorksheetFilters
            sheet={sheet}
            sheetPlan={sheetPlan}
            column={column}
            change={change}
            removals={removals}
            stale={stale}
            distinct={distinct}
            notePending={notePending}
          />
        </fieldset>
        {validations && (
          <WorksheetColumnValidations
            sheet={sheet}
            column={column}
            type={validations.type}
            states={validations.states}
            readOnly={readOnly}
            change={change}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}
